import type { ConnectionStatus, NotificationEvent, NotificationType } from "../types";
import type {
  ConnectionStatusHandler,
  GetTokenFn,
  NotificationHandler,
  NotificationsClient,
} from "./types";

/**
 * Cliente fake. Não abre conexão real. Em desenvolvimento serve para validar a
 * UI sem depender do stream SSE do backend — emite eventos sintéticos em
 * intervalos variáveis (e um burst inicial logo após start).
 */

const INITIAL_BURST: NotificationEvent[] = [
  {
    type: "SupportReplied",
    payload: {
      ticketId: "tk-1",
      ticketCode: "TK-512",
      subject: "Dúvida sobre a fatura de maio",
    },
  },
  {
    type: "BrokerJoined",
    payload: { brokerId: "b-mock-01", brokerName: "Juliana Prado" },
  },
  {
    type: "SeatsLow",
    payload: { availableSlots: 2, planName: "Agency Pro" },
  },
];

const RECURRING_POOL: () => NotificationEvent[] = () => [
  {
    type: "BrokerJoined",
    payload: {
      brokerId: `b_${Date.now()}`,
      brokerName: pick([
        "Felipe Nogueira",
        "Camila Rocha",
        "Tatiana Borges",
        "Eduardo Lima",
      ]),
    },
  },
  {
    type: "LicenseActivated",
    payload: {
      brokerId: `b_${Date.now()}`,
      brokerName: pick(["Marcos Oliveira", "Rafaela Souza", "Bruno Sá"]),
    },
  },
  {
    type: "SupportReplied",
    payload: {
      ticketId: pick(["tk-1", "tk-2", "tk-3"]),
      ticketCode: pick(["TK-512", "TK-498", "TK-471"]),
      subject: pick([
        "Corretor não consegue acessar o app",
        "Dúvida sobre a fatura de maio",
        "Sugestão: relatório por equipe",
      ]),
    },
  },
];

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)] as T;
}

class MockNotificationsClient implements NotificationsClient {
  private status: ConnectionStatus = "disconnected";
  private statusHandlers: ConnectionStatusHandler[] = [];
  private handlers = new Map<NotificationType, NotificationHandler<NotificationType>[]>();
  private burstTimers: number[] = [];
  private recurringTimer: number | null = null;
  private built = false;

  build(_getToken: GetTokenFn): void {
    void _getToken;
    this.built = true;
  }

  async start(): Promise<void> {
    if (!this.built) {
      throw new Error("Mock client not built. Call build() first.");
    }
    this.emitStatus("reconnecting");
    // Simula latência de handshake
    await new Promise((r) => setTimeout(r, 400));
    this.emitStatus("connected");

    // Burst inicial: dispara os eventos ao longo dos primeiros segundos
    INITIAL_BURST.forEach((event, i) => {
      const t = window.setTimeout(() => this.dispatch(event), 1500 * (i + 1));
      this.burstTimers.push(t);
    });

    // Recorrente: a cada 20–40s manda 1 evento aleatório
    this.scheduleNextRecurring();
  }

  async stop(): Promise<void> {
    this.burstTimers.forEach((t) => window.clearTimeout(t));
    this.burstTimers = [];
    if (this.recurringTimer !== null) {
      window.clearTimeout(this.recurringTimer);
      this.recurringTimer = null;
    }
    this.emitStatus("disconnected");
  }

  on<T extends NotificationType>(event: T, handler: NotificationHandler<T>): void {
    const list = this.handlers.get(event) ?? [];
    list.push(handler as unknown as NotificationHandler<NotificationType>);
    this.handlers.set(event, list);
  }

  off(event: NotificationType): void {
    this.handlers.delete(event);
  }

  getConnectionState(): ConnectionStatus {
    return this.status;
  }

  onStatusChange(handler: ConnectionStatusHandler): void {
    this.statusHandlers.push(handler);
  }

  private dispatch(event: NotificationEvent): void {
    const list = this.handlers.get(event.type);
    if (!list) return;
    for (const h of list) {
      try {
        (h as NotificationHandler<NotificationType>)(event.payload);
      } catch (err) {
        console.error("[sse-mock] handler threw:", err);
      }
    }
  }

  private scheduleNextRecurring(): void {
    const delay = 20_000 + Math.random() * 20_000;
    this.recurringTimer = window.setTimeout(() => {
      if (this.status === "connected") {
        const pool = RECURRING_POOL();
        const event = pool[Math.floor(Math.random() * pool.length)] as NotificationEvent;
        this.dispatch(event);
      }
      this.scheduleNextRecurring();
    }, delay);
  }

  private emitStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const h of this.statusHandlers) {
      try {
        h(status);
      } catch (err) {
        console.error("[sse-mock] status handler threw:", err);
      }
    }
  }
}

export const mockNotificationsClient: NotificationsClient = new MockNotificationsClient();
